import { Spin } from "antd";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGE } from "services/general";
import {
  ChatChevronIcon,
  ChatCloseIcon,
  ChatCopyIcon,
  ChatSearchIcon,
  ChatTrashIcon,
} from "components/shared/svg-action-icons";

const ChatThreadsPanel = ({
  variant = "overlay",
  isOpen,
  onClose,
  onCloseChat,
  searchQuery,
  onSearchChange,
  groupedThreads,
  activeThreadId,
  threadsFetching,
  threadsLoading,
  threadsPage,
  threadsContainerRef,
  onThreadsScroll,
  onSelectThread,
  onCopyThread,
  onDeleteThread,
  getThreadUuid,
}) => {
  const { t } = useTranslation();
  const isOverlay = variant === "overlay";
  const isRtl = LANGUAGE === "ar";

  if (isOverlay && !isOpen) return null;

  const panelContent = (
    <>
      {!isOverlay && (
        <div className="p-4 border-b border-border-light">
          <div className="relative flex items-center bg-surface-muted rounded-full px-3 py-2 border border-border-light">
            <ChatSearchIcon className="text-text-subtle" />
            <input
              type="text"
              placeholder={t("chat.search_threads")}
              className="bg-transparent border-none text-xs text-text-body placeholder-text-subtle focus:outline-none w-full ms-2"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>
      )}

      {isOverlay && (
        <>
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-light flex-shrink-0">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="p-1 text-secondary hover:text-text-strong rounded-md hover:bg-surface-muted transition"
              >
                <ChatChevronIcon direction={isRtl ? "end" : "start"} />
              </button>
              <span className="text-[14px] font-bold text-text-strong">
                {t("chat.thread_history")}
              </span>
            </div>
            <button
              type="button"
              onClick={onCloseChat}
              className="p-1 text-secondary hover:text-text-strong rounded-md hover:bg-surface-muted transition"
            >
              <ChatCloseIcon />
            </button>
          </div>

          <div className="px-4 py-3 border-b border-border-light flex-shrink-0">
            <div className="relative flex items-center bg-surface-muted rounded-full px-3 py-1.5 border border-border-light">
              <ChatSearchIcon className="text-text-subtle" />
              <input
                type="text"
                placeholder={t("chat.search_threads")}
                className="bg-transparent border-none text-xs text-text-body placeholder-text-subtle focus:outline-none w-full ms-2"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          </div>
        </>
      )}

      <div
        ref={threadsContainerRef}
        onScroll={onThreadsScroll}
        className={`flex-1 overflow-y-auto p-4 space-y-4 bar ${
          isOverlay ? "" : ""
        }`}
      >
        {groupedThreads.length === 0 ? (
          threadsFetching ? (
            <div className={`flex justify-center ${isOverlay ? "py-8" : "py-12"}`}>
              <Spin size="small" />
            </div>
          ) : (
            <div
              className={`text-center text-text-subtle text-xs ${
                isOverlay ? "py-8" : "py-12"
              }`}
            >
              {t("chat.no_threads")}
            </div>
          )
        ) : (
          groupedThreads.map((group) => (
            <div key={group.id} className="space-y-1.5">
              <h4 className="text-[11px] font-bold text-text-subtle text-start uppercase px-2">
                {group.title}
              </h4>
              <div className="space-y-1">
                {group.data.map((thread) => {
                  const threadUuid = getThreadUuid(thread);
                  const isActive = activeThreadId === threadUuid;

                  return (
                    <div
                      key={thread.id || threadUuid}
                      onClick={() => onSelectThread(threadUuid)}
                      className={`group flex items-center justify-between rounded-xl border border-transparent cursor-pointer transition text-start ${
                        isOverlay ? "p-2" : "p-2.5"
                      } ${
                        isActive
                          ? "bg-primary/5 border-primary/20"
                          : "hover:bg-surface-muted"
                      }`}
                      style={{
                        borderInlineStartWidth: isActive ? "3px" : "1px",
                        borderInlineStartColor: isActive
                          ? "rgb(var(--color-primary))"
                          : "transparent",
                      }}
                    >
                      <div className="flex-1 min-w-0 pe-2">
                        <p className="text-xs font-bold text-text-body truncate mb-0.5">
                          {thread.title || t("chat.untitled_thread")}
                        </p>
                        <p className="text-[10px] text-text-subtle truncate mb-0">
                          {thread.last_message_preview ||
                            t("chat.no_description")}
                        </p>
                      </div>

                      <div
                        className={`flex items-center gap-1 flex-shrink-0 ${
                          isOverlay
                            ? "opacity-60 hover:opacity-100"
                            : "opacity-0 group-hover:opacity-100"
                        } transition`}
                      >
                        <button
                          type="button"
                          onClick={(e) => onCopyThread(e, threadUuid)}
                          title={t("chat.copy_thread_id")}
                          className="p-1 text-secondary hover:text-primary rounded hover:bg-surface transition"
                        >
                          <ChatCopyIcon />
                        </button>
                        <button
                          type="button"
                          onClick={(e) =>
                            onDeleteThread(
                              e,
                              thread.id || threadUuid,
                              threadUuid
                            )
                          }
                          className="p-1 text-secondary hover:text-danger rounded hover:bg-surface transition"
                        >
                          <ChatTrashIcon />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}

        {(threadsLoading || (threadsFetching && threadsPage > 1)) && (
          <div className="flex justify-center p-2">
            <Spin size="small" />
          </div>
        )}
      </div>
    </>
  );

  if (isOverlay) {
    return (
      <div className="absolute inset-0 bg-surface z-20 flex flex-col animate-slide-in">
        {panelContent}
      </div>
    );
  }

  return (
    <div className="w-[280px] bg-surface border-e border-border-light flex flex-col flex-shrink-0 animate-fade-in relative z-10 h-full">
      {panelContent}
    </div>
  );
};

export default memo(ChatThreadsPanel);
